import React, { useEffect, useMemo, useState } from 'react';
import { Platform, View, StyleSheet, useWindowDimensions } from 'react-native';
import { Text } from './Text';
import type { NavigationContainerRefWithCurrent } from '@react-navigation/native';
import HomeScreen from '../screens/HomeScreen';
import { makeStyles, useTheme, radius, ThemeScope, inSidebar } from '../theme';
import type { RootStackParamList } from '../navTypes';
import { SHELL_ID, SHELL_MAIN_ID } from './webChrome';
import { WorkspaceRail, useScope } from './WorkspaceSwitcher';

/* Desktop shell — web only, wide windows only.
   The phone Home screen becomes the left sidebar (it is already a narrow column), and the
   navigation stack fills the right pane, so every existing screen renders there unchanged with
   its own header. Native builds never take this branch: useIsDesktop() is false off the web, and
   App.tsx keeps the phone navigator tree exactly as it was. */

export const DESKTOP_MIN_WIDTH = 900;
export const SIDEBAR_WIDTH = 340;

export function useIsDesktop(): boolean {
  const { width } = useWindowDimensions();
  return Platform.OS === 'web' && width >= DESKTOP_MIN_WIDTH;
}

type NavRef = NavigationContainerRefWithCurrent<RootStackParamList>;
const ROOM_ROUTES = new Set(['Chat', 'Thread', 'Threads', 'RoomInfo']);

/** The room the right pane currently shows, read from the navigator's state. */
export function useActiveRoomId(navRef: NavRef): string | undefined {
  const [roomId, setRoomId] = useState<string | undefined>(undefined);
  useEffect(() => {
    const read = () => {
      const r = navRef.isReady() ? navRef.getCurrentRoute() : undefined;
      const p = (r?.params || {}) as { roomId?: string };
      setRoomId(r && ROOM_ROUTES.has(r.name) && p.roomId ? String(p.roomId) : undefined);
    };
    read();
    return navRef.addListener('state', read);
  }, [navRef]);
  return roomId;
}

export function DesktopShell({ navRef, children }: { navRef: NavRef; children: React.ReactNode }) {
  const s = useStyles();
  const activeRoomId = useActiveRoomId(navRef);
  const { show: rail } = useScope(); // two workspaces or more: they are chosen on a rail left of the list
  // HomeScreen only ever calls navigation.navigate(...); the container ref answers that.
  const navigation = useMemo(() => ({
    navigate: (...args: unknown[]) => { if (navRef.isReady()) (navRef.navigate as unknown as (...a: unknown[]) => void)(...args); },
  }), [navRef]);
  return (
    <View style={s.row} nativeID={SHELL_ID}>
      <WorkspaceRail />
      <View style={s.sidebar}>
        {/* Inside the sidebar `bg` is the sidebar's canvas and `surface` what its cards sit on. */}
        <ThemeScope.Provider value={inSidebar}>
          <HomeScreen
            navigation={navigation as unknown as React.ComponentProps<typeof HomeScreen>['navigation']}
            route={{ key: 'sidebar', name: 'Home' } as unknown as React.ComponentProps<typeof HomeScreen>['route']}
            activeRoomId={activeRoomId} rail={rail}
          />
        </ThemeScope.Provider>
      </View>
      <View style={s.main} nativeID={SHELL_MAIN_ID}>{children}</View>
    </View>
  );
}

/** The right pane before a conversation is chosen. Registered only in the desktop tree. */
export function WelcomeScreen() {
  const theme = useTheme(); const s = useStyles();
  return (
    <View style={s.welcome}>
      <View style={s.logo}><Text style={s.logoText}>H</Text></View>
      <Text style={s.wTitle}>Pick a conversation</Text>
      <Text style={s.wSub}>Rooms and threads are on the left. Anything that needs your approval sits at the top of that list.</Text>
      <Text style={[s.wSub, { color: theme.faint, marginTop: 18 }]}>Type in the search box to find a room by name.</Text>
    </View>
  );
}

const useStyles = makeStyles((theme) => StyleSheet.create({
  row: { flex: 1, flexDirection: 'row', backgroundColor: theme.bg, overflow: 'hidden' },
  sidebar: { width: SIDEBAR_WIDTH, borderRightWidth: 1, borderRightColor: theme.border, backgroundColor: theme.sidebar },
  main: { flex: 1, minWidth: 0, backgroundColor: theme.bg, overflow: 'hidden' },
  welcome: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 40, backgroundColor: theme.bg },
  logo: { width: 52, height: 52, borderRadius: radius.lg, backgroundColor: theme.accent, alignItems: 'center', justifyContent: 'center', marginBottom: 18 },
  logoText: { color: theme.accentInk, fontWeight: '800', fontSize: 22 },
  wTitle: { color: theme.ink, fontSize: 22, fontWeight: '700' },
  wSub: { color: theme.muted, fontSize: 15, textAlign: 'center', maxWidth: 420, marginTop: 8, lineHeight: 22 },
}));
